/* Copyright (c) 2020-present CloudIO Inc */

import {
  AnyItem,
  ColumnType,
  CommonProps,
  defaultCommonProps,
  getComponentNameMap,
  getManager,
  Item,
  ItemID,
  ItemType,
  Padding,
  Page,
  Responsive,
} from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import {
  action,
  atom,
  selector,
  StateXGetter,
  StateXSetter,
} from '@cloudio-saas/statex';
import { itemExpandedSelector } from 'layout/Sidebar/itemExpandedSelector';
import {
  getDefaultValues,
  getPropertyDef,
} from 'layout/Sidebar/panels/PropertiesView/property-editors/PropertyEditorUtils';
import { TreeTypeDesigner } from 'layout/Sidebar/panels/TreeView/state';
import type { XYCoord } from 'react-dnd';
import { getErrorMessage, isNull, NOT_DEFINED } from 'utils/isEmpty';
import { memoize } from 'utils/memoPreviousValue';
import { rootPageWidthAtom, windowSizeAtom } from 'hooks/useWindowSize';
import { getScreenSize } from 'hooks/useScreenSize';
import { createItem, itemWithDefaultValues } from 'registry/Components';
import {
  getFilterDataType,
  isBooleanType,
  isDateType,
  isNumberType,
  isStringType,
  RunTimeProps,
  SmartSearchGridData,
} from '@cloudio-saas/datasource-types';
import Store from 'store/Store';
import { evalX } from '@cloudio-saas/quickx';
import { ulid } from 'ulid';
import PlatformVariableProvider from '../expression/VariableProvider';
import { activeItemSelector } from './activeItemSelector';
import {
  childrenAtom,
  childrenRTAtom,
  copyItemAtom,
  designPath,
  developerModeAtom,
  // dragHoverIdAtom,
  dragItemAtom,
  dragTipAtom,
  draggingAtom,
  embeddedPagesAtom,
  formulaChildrenRTAtom,
  formulaItemDataAtom,
  hmrAtom,
  hoverItemsAtom,
  initialXYCoordAtom,
  itemPropPath,
  itemTypeAtom,
  pageAtom,
  prevHoverIdAtom,
  prevTimeAtom,
  prevXYCoordAtom,
  runtimeItemAtom,
  runtimeItemPropAtom,
  runtimePath,
  showHiddenItemsAtom,
  smartSearchChildrenRTAtom,
  itemAtom,
  clipboardIdAtom,
  pivotConfigAtom,
  formulaItemPropsAtom,
} from './atoms';
import { Column } from '../custom/PivotGrid/PivotGridTypes';
import {
  defaultOperators,
  SmartSearchIdType,
  toAttributeDataType,
} from '../componentregistry/components/SmartSearch/SmartSearch';
import { isWhoAttribute } from 'layout/Sidebar/atoms';
import AbortError from 'platform/AbortError';

export const helpTextSelector = selector<string>({
  path: [
    'transient',
    'designer',
    'helpText',
    ':appUid',
    ':pageId',
    ':itemId',
    ':type',
    ':property',
  ],
  get: ({ get, params }) => {
    if (!params) return '';
    const { appUid, pageId, itemId, property } = params;
    get(hmrAtom);
    const item = get(itemAtom, { params: { appUid, pageId, itemId } });
    if (!item) {
      // item may have been deleted
      // console.warn(`Missing props for item ${itemId}`);
      return '';
    }
    const mgr = getManager(item.type as ItemType);
    const def = getPropertyDef(mgr, property as string);
    if (!def) {
      // property may have been removed. happens when installing a different version of the custom component
      return '';
    }
    let val = '';
    const itemWithDefaults = itemWithDefaultValues(item);
    if (typeof def.helpText === 'function') {
      val = def.helpText(itemWithDefaults);
    } else if (def.helpText) {
      val = def.helpText;
    }
    return val;
  },
});

export const propertySelector = selector({
  path: [
    'transient',
    'designer',
    'property',
    ':appUid',
    ':pageId',
    ':itemId',
    ':prop',
  ],
  get: ({ get, params }) => {
    if (!params) return undefined;
    const { appUid, itemId, prop, pageId } = params;
    if (itemId === '') {
      return undefined;
    }
    // first check for runtime prop
    let value = get(runtimeItemPropAtom, {
      params: { appUid, pageId, itemId, prop },
    });
    if (value !== undefined && value !== null) {
      // runtime prop has been set... use it
      return value;
    }
    value = get(itemPropPath, {
      params: { appUid, pageId, itemId, prop },
    });
    if (value === undefined || value === null) {
      const type = get(itemTypeAtom, {
        params: { appUid, pageId, itemId },
      });
      if (!type) {
        return undefined;
      }
      const mgr = getManager(type);
      return getDefaultValues(mgr, prop as string);
    }
    return value;
  },
});

export const bulkPropertySelector = selector<unknown[], string[]>({
  path: [
    'transient',
    'designer',
    'bulkProperty',
    ':appUid',
    ':pageId',
    ':itemId',
    ':prop',
  ],
  get: ({ get, params }, ids: string[]) => {
    if (!params) return [];
    const { appUid, prop, pageId } = params;
    return ids.map((itemId) => {
      // first check for runtime prop
      let value = get(runtimeItemPropAtom, {
        params: { appUid, pageId, itemId, prop },
      });
      if (!isNull(value)) {
        // runtime prop has been set... use it
        return value;
      }
      value = get(itemPropPath, {
        params: { appUid, pageId, itemId, prop },
      });
      if (!isNull(value)) {
        // runtime prop has been set... use it
        return value;
      }
      value = get(formulaItemPropsAtom, {
        params: { appUid, pageId, itemId, prop },
      });
      if (!isNull(value)) {
        // runtime prop has been set... use it
        return value;
      }
      const itemType = get(itemTypeAtom, {
        params: { appUid, pageId, itemId },
      });
      const mgr = getManager(itemType);
      if (mgr) {
        // use the default value
        return mgr.defaultValue[prop];
      }
      return value;
    });
  },
});

const visibleRTPath = [...runtimePath({}), ':itemId', 'visible'];
const visiblePath = [...designPath({}), ':itemId', 'props', 'visible'];
const itemTypePath = [...designPath({}), ':itemId', 'type'];
const visibleConditionRTPath = [
  ...runtimePath({}),
  ':itemId',
  'visibleCondition',
];
const responsivePath = [...designPath({}), ':itemId', 'props', 'responsive'];
const responsiveRTPath = [...runtimePath({}), ':itemId', 'responsive'];
const visibleConditionPath = [
  ...designPath({}),
  ':itemId',
  'props',
  'visibleCondition',
];
const disableConditionRTPath = [
  ...runtimePath({}),
  ':itemId',
  'disableCondition',
];
const disableConditionPath = [
  ...designPath({}),
  ':itemId',
  'props',
  'disableCondition',
];

export const EMPTY_STRING_ARRAY: string[] = [];
const EMPTY_ITEM_TYPE_ARRAY: ItemType[] = [];

export const EMPTY_RESPONSE = {
  children: EMPTY_STRING_ARRAY,
  childrenTypes: EMPTY_ITEM_TYPE_ARRAY,
};

export const activeTriggerSelector = selector<boolean, Platform>({
  path: [
    'transient',
    'designer',
    'activeTrigger',
    ':appUid',
    ':pageId',
    ':itemId',
    ':trigger',
  ],
  get: ({ get, getRef, params }, platform) => {
    if (!params) return false;
    const { appUid, pageId, itemId, trigger } = params as Record<
      string,
      string
    >;
    // this will also subscribe to the children prop
    // and re-render when the children change
    let children = get(childrenAtom, { params: { appUid, pageId, itemId } });
    if (!children || children.length === 0) return false;
    if (!platform) {
      // this should never happen
      throw Error('Missing platform ref!');
    }

    // this will also subscribe to all children visible prop changes
    // and re-render when any of the child's visible prop changes
    children = children.filter((c) => {
      const type = get(itemTypeAtom, {
        params: { appUid, pageId, itemId: c },
      });
      if (type.indexOf(trigger) === -1) {
        return false;
      }
      // first check for runtime prop
      let disableCondition = get<string>(disableConditionRTPath, {
        params: { appUid, pageId, itemId: c },
      });
      if (disableCondition === undefined || disableCondition === null) {
        // runtime prop has not been set...
        // check design prop...
        disableCondition = get<string>(disableConditionPath, {
          params: { appUid, pageId, itemId: c },
        });
      }
      let disabled = false;
      if (disableCondition) {
        // disableCondition exists... evaluate it
        try {
          const pp = new PlatformVariableProvider(
            platform,
            {
              appUid,
              pageId,
              itemId: c,
              property: 'disableCondition',
              data: {},
              dsAlias: NOT_DEFINED,
              rowIndex: -1,
            },
            get,
          );
          disabled = !!evalX(disableCondition, pp);
        } catch (e) {
          platform.showError(
            e instanceof Error
              ? `${e.name} evaluating expression [${disableCondition}]: ${e.message}`
              : getErrorMessage(e),
            { appUid, pageId, itemId },
          );
          disabled = true;
        }
      }
      return !disabled;
    });
    return children.length > 0;
  },
});

function merge(
  children: string[],
  rtChildren: string[],
  formulaItemIds: string[],
) {
  let retChildren = [...rtChildren];
  // handle removed children
  retChildren = retChildren.filter(
    (cid) => formulaItemIds.includes(cid) || children.includes(cid),
  );
  // handle new children in design but not in run time, insert each new children beforn the next child
  for (let i = children.length - 1; i >= 0; i--) {
    if (!retChildren.includes(children[i])) {
      if (i === children.length - 1) {
        retChildren.push(children[i]);
      } else {
        const index = retChildren.indexOf(children[i + 1]);
        retChildren = [
          ...retChildren.slice(0, index),
          children[i],
          ...retChildren.slice(index),
        ];
      }
    }
  }
  return retChildren;
}

const getRuntimeChildren = (
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
  get: StateXGetter,
): string[] => {
  const children = get(childrenAtom, { params: { appUid, pageId, itemId } });
  const formulaChildren = get(formulaChildrenRTAtom, {
    params: { appUid, pageId, itemId },
  });
  const smartSearchChildren = [
    ...get(smartSearchChildrenRTAtom, {
      params: { appUid, pageId, itemId },
    }),
  ];
  if (!(formulaChildren.length || smartSearchChildren.length)) {
    return [];
  }
  let runtimeChildren: string[] = [];

  runtimeChildren = smartSearchChildren.length
    ? smartSearchChildren
    : formulaChildren;

  runtimeChildren = merge(children, runtimeChildren, formulaChildren);
  //  remove children in smart search but neither in design or in formula items

  // for formula items not found in smartsearch, add them in apporopriate index

  return runtimeChildren;
};

export function setFormulaItems(
  appUid: string,
  pageId: string,
  itemId: string,
  formulaItems: Record<string, AnyItem>,
  rtChildren: string[],
  runtimeProps: Record<string, RunTimeProps>,
  set: StateXSetter,
  get: StateXGetter,
) {
  const formulaItemIds = Object.keys(formulaItems);
  let children = get(childrenAtom, { params: { appUid, pageId, itemId } });
  children = merge(children, rtChildren, formulaItemIds); // merge childrenWithForumlaColumns into children

  set(formulaChildrenRTAtom, children, { params: { appUid, pageId, itemId } });
  set(childrenRTAtom, getRuntimeChildren(appUid, pageId, itemId, get), {
    params: { appUid, pageId, itemId },
  });

  formulaItemIds.forEach((formulaItemId) => {
    set(formulaItemDataAtom, formulaItems[formulaItemId], {
      params: { appUid, pageId, itemId: formulaItemId },
    });
  });
}

export function setGridMetaData({
  appUid,
  get,
  gridData,
  gridId,
  idType,
  pageId,
  pivotId,
  set,
}: {
  appUid: string;
  get: StateXGetter;
  gridData: SmartSearchGridData | undefined;
  gridId: string;
  idType: SmartSearchIdType;
  pageId: string;
  pivotId: string;
  set: StateXSetter;
}) {
  if (!gridData) {
    resetGridMetaData({
      appUid,
      get,
      gridData,
      gridId,
      idType,
      pageId,
      pivotId,
      set,
    });
    return;
  }

  if (idType === 'DataGrid') {
    const children = gridData.runtimeChildren ?? [];
    set(smartSearchChildrenRTAtom, children, {
      params: { appUid, pageId, itemId: gridId },
    });
    set(childrenRTAtom, getRuntimeChildren(appUid, pageId, gridId, get), {
      params: { appUid, pageId, itemId: gridId },
    });
    if (gridData.runtimeProps) {
      const runtimeProps = gridData.runtimeProps;
      Object.keys(runtimeProps).forEach((id) => {
        set(visibleRTPath, runtimeProps[id].visible, {
          params: { appUid, pageId, itemId: id },
        });
      });
    }
  } else if (gridData.pivotConfig) {
    set(pivotConfigAtom, gridData.pivotConfig, {
      params: { appUid, pageId, itemId: pivotId },
    });
  }
}

export function resetGridMetaData({
  appUid,
  get,
  gridData,
  gridId,
  idType,
  pageId,
  pivotId,
  set,
}: {
  appUid: string;
  get: StateXGetter;
  gridData: SmartSearchGridData | undefined;
  gridId: string;
  idType: SmartSearchIdType;
  pageId: string;
  pivotId: string;
  set: StateXSetter;
}) {
  const children = getRuntimeChildren(appUid, pageId, gridId, get);
  set(smartSearchChildrenRTAtom, [], {
    params: { appUid, pageId, itemId: gridId },
  });
  set(childrenRTAtom, children, {
    params: { appUid, pageId, itemId: gridId },
  });

  children.forEach((id) => {
    const isVisible = get(visibleRTPath, {
      params: { appUid, pageId, itemId: id },
    });
    if (isVisible === false) {
      set(visibleRTPath, true, {
        params: { appUid, pageId, itemId: id },
      });
    }
  });
}

export const getChildren = (
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
  get: StateXGetter,
): [ItemID[], ItemType[]] => {
  const rtchildren = get(developerModeAtom, {
    params: { appUid },
  })
    ? undefined
    : get(childrenRTAtom, { params: { appUid, pageId, itemId } });
  let children = get(childrenAtom, {
    params: { appUid, pageId, itemId },
  });
  if (rtchildren) {
    let formulaChildren = get(formulaChildrenRTAtom, {
      params: { appUid, pageId, itemId },
    });
    // formulaChildren = []; // TODO: need to remove this once formulaChildrenRTAtom is fixed
    formulaChildren = formulaChildren.filter((id) => id.startsWith('f_'));
    children = merge(children, rtchildren, formulaChildren);
  }
  if (!children || children.length === 0)
    return [EMPTY_STRING_ARRAY, EMPTY_ITEM_TYPE_ARRAY];

  // we need to re-create the child element if any of the child type changes
  // hence, we need to subscribe to the type of all children
  const childrenTypes = children.map(
    (c) =>
      get(itemTypePath, {
        params: { appUid, pageId, itemId: c },
      }) as ItemType,
  );

  return [children, childrenTypes];
};

export const emptyVisibleChildrenSelector = selector<
  { children: ItemID[]; childrenTypes: ItemType[] },
  { platform: Platform; rowIndex: number | undefined; dsAlias: string }
>({
  path: [
    'transient',
    'designer',
    'emptyVisibleChildren',
    ':appUid',
    ':pageId',
    ':itemId',
    ':rowIndex',
  ],
  get: () => ({
    children: EMPTY_STRING_ARRAY,
    childrenTypes: EMPTY_ITEM_TYPE_ARRAY,
  }),
});

export const visibleChildrenSelector = selector<
  { children: ItemID[]; childrenTypes: ItemType[] },
  {
    platform: Platform;
    rowIndex: number | undefined;
    dsAlias: string;
    itemType?: ItemType;
  }
>({
  path: [
    'transient',
    'designer',
    'visibleChildren',
    ':appUid',
    ':pageId',
    ':itemId',
    ':rowIndex',
  ],
  get: (
    { get, params, getOnce },
    { platform, rowIndex, dsAlias, itemType },
  ) => {
    if (!params) return EMPTY_RESPONSE;
    const { appUid, pageId, itemId } = params as Record<string, string>;
    if (!itemId) {
      return EMPTY_RESPONSE;
    }
    // this will also subscribe to the children prop
    // and re-render when the children change
    const result = getChildren(appUid, pageId, itemId, get);
    let children = result[0];
    const childrenTypes = result[1];
    if (children.length === 0) return EMPTY_RESPONSE;
    const developerMode = get(developerModeAtom, {
      params: { appUid },
    });
    const embedded = get(embeddedPagesAtom);
    const showHiddenItems = get(showHiddenItemsAtom);
    if (
      developerMode &&
      !embedded.includes(pageId.toString()) &&
      showHiddenItems
    ) {
      return { children, childrenTypes };
    }
    if (!platform) {
      // this should never happen
      throw Error('Missing platform ref!');
    }

    // this will also subscribe to all children visible prop changes
    // and re-render when any of the child's visible prop changes
    children = children.filter((c) => {
      // if itemType is provided, check if the child is of the same type
      // if not, return false. This is used to filter out children of a
      // different type when we are looking for a specific type of child
      // e.g. useVisibleChildrenOfType
      if (itemType) {
        // itemTypePath value will never change and hence using getOnce
        const childType = getOnce(itemTypePath, {
          params: { appUid, pageId, itemId: c },
        });
        if (childType !== itemType) {
          return false;
        }
      }

      // first check for runtime prop
      let visible = get(visibleRTPath, {
        params: { appUid, pageId, itemId: c },
      });
      if (visible === undefined || visible === null) {
        // runtime prop has not been set...
        // check design prop... or else default to true
        visible =
          get(visiblePath, { params: { appUid, pageId, itemId: c } }) ?? true;
      }
      if (visible) {
        // check visibleCondition
        // first check for runtime prop
        let visibleCondition = get<string>(visibleConditionRTPath, {
          params: { appUid, pageId, itemId: c },
        });
        if (visibleCondition === undefined || visible === null) {
          // runtime prop has not been set...
          // check design prop...
          visibleCondition = get<string>(visibleConditionPath, {
            params: { appUid, pageId, itemId: c },
          });
        }
        if (visibleCondition) {
          // visibleCondition exists... evaluate it
          try {
            const pp = new PlatformVariableProvider(
              platform,
              {
                appUid,
                pageId,
                itemId: c,
                property: 'visibleCondition',
                data: {},
                dsAlias,
                rowIndex: rowIndex ?? -1,
              },
              get,
            );
            visible = !!evalX(visibleCondition, pp);
          } catch (e) {
            platform.showError(
              e instanceof Error
                ? `${e.name} evaluating expression [${visibleCondition}]: ${e.message}`
                : getErrorMessage(e),
            );
            visible = false;
          }
        }
      }

      if (visible) {
        let responsive = get<Responsive>(responsiveRTPath, {
          params: { appUid, pageId, itemId: c },
        });
        if (responsive === undefined) {
          // runtime prop has not been set...
          // check design prop...
          responsive = get<Responsive>(responsivePath, {
            params: { appUid, pageId, itemId: c },
          });
        }
        if (responsive) {
          const { lg, md, sm, xl, xs } = responsive;
          const state = get(sidebarStateAtom, { params: { appUid } });
          const width =
            get(rootPageWidthAtom) +
            (state.exists && state.open ? state.width : 0);
          const size = get(windowSizeAtom);
          const screenSize = getScreenSize({ width, height: size.height });
          if (lg && screenSize.lg) {
            return false;
          }
          if (md && screenSize.md) {
            return false;
          }
          if (sm && screenSize.sm) {
            return false;
          }
          if (xl && screenSize.xl) {
            return false;
          }
          if (xs && screenSize.xs) {
            return false;
          }
        }
      }
      // const currentState = visibilityCache.get([pageId, c]) ?? true;
      // if (visible !== currentState) {
      //   visibilityCache.set([pageId, c], visible);
      // }
      return visible;
    });
    return {
      children,
      childrenTypes,
    };
  },
});

const { withKey } = memoize();

export const visibleChildItemsSelector = selector<
  AnyItem[],
  { platform: Platform; rowIndex: number | undefined; dsAlias: string }
>({
  path: [
    'transient',
    'designer',
    'visibleChildItems',
    ':appUid',
    ':pageId',
    ':itemId',
    ':rowIndex',
  ],
  get: ({ get, params }, { platform, rowIndex, dsAlias }) => {
    if (!params) return [];
    const { appUid, pageId } = params;
    const { children } = get(visibleChildrenSelector, {
      params,
      props: { platform, rowIndex, dsAlias },
    });
    const items = children
      .map((c) => {
        let item = get<AnyItem>(designPath({ appUid, pageId, itemId: c }));

        if (!item) {
          const formlaItem = get(formulaItemDataAtom, {
            params: { appUid, pageId, itemId: c },
          });
          if (formlaItem) {
            item = formlaItem;
          }
        }

        if (!item) {
          // item may have been just deleted
          return null;
          // // this should never happen
          // throw Error(`Missing item with id ${c}!`);
        }

        const mgr = getManager(item.type);
        const runtimeItem = get(runtimeItemAtom, {
          params: { appUid, pageId, itemId: item.itemId },
        });
        return withKey(`${item.itemId}-${pageId}-vcis`)<AnyItem>(() => {
          // Changed to Object.assign instead of object spread to avoid
          // TS2590: Expression produces a union type that is too complex to represent
          // eslint-disable-next-line prefer-object-spread
          const props = Object.assign(
            {},
            defaultCommonProps,
            mgr.defaultValue,
            item.props,
            runtimeItem,
          ) as CommonProps;
          return { ...item, props } as AnyItem;
        }, [item, runtimeItem]);
      })
      .filter((item) => !!item) as AnyItem[];

    return items;
  },
});

export const onDragStartAction = action(({ set }, item: AnyItem) => {
  set(draggingAtom, true);
  set(dragItemAtom, item.itemId);
  // if (item) {
  //   set(dragHoverIdAtom, item.parent);
  // }
});

export const onDragEndAction = action(({ set, reset }) => {
  set(draggingAtom, false);
  reset(dragItemAtom);
  // reset(dragHoverIdAtom);
  reset(prevHoverIdAtom);
  reset(prevXYCoordAtom);
  reset(dragTipAtom);
});

// export const onMouseMoveAction = action(
//   (
//     { get, set },
//     { itemId, event }: { itemId: string; event: React.MouseEvent<HTMLElement> },
//   ) => {
//     if (get(draggingAtom)) {
//       const dragItemId = get(dragItemAtom);
//       const dragHoverId = get(dragHoverIdAtom);
//       if (dragItemId !== itemId && dragHoverId !== itemId) {
//         event.stopPropagation();
//         set(dragHoverIdAtom, itemId);
//       }
//     }
//   },
// );

function move(props: {
  set: StateXSetter;
  appUid: string;
  pageId: string;
  page: Page;
  parent: AnyItem;
  item: AnyItem;
  hoverId?: string;
}) {
  const { appUid, set, pageId, hoverId } = props;
  const parent = { ...props.parent };
  const item = { ...props.item };
  const page = { ...props.page };
  // add item inside hoverItem's parent
  let { children } = parent;
  if (hoverId) {
    const index = children.indexOf(hoverId);
    children = insert(children, index, item.itemId);
  } else {
    children = [...children, item.itemId];
  }
  // expand the parent node in the tree view
  set(itemExpandedSelector, true, {
    params: { appUid, pageId, itemId: parent.itemId, treename: 'designer' },
  });
  parent.children = children;

  if (item.parent !== '') {
    // remove item from currentParent
    const currentParent = page[item.parent];
    const _currentParent = { ...page[item.parent] };
    _currentParent.children = _currentParent.children.filter(
      (c) => c !== item.itemId,
    );
    getManager(_currentParent.type).onChildMoveOut({
      appUid,
      child: item,
      oldValue: currentParent,
      page,
      pageId,
      value: _currentParent,
    });
    page[_currentParent.itemId] = _currentParent;
  }
  // update item's parent with hoverItem's parent
  item.parent = parent.itemId;
  // update the updated parent & item
  page[parent.itemId] = parent;
  page[item.itemId] = item;
  getManager(parent.type).onChildMoveIn({
    appUid,
    child: item,
    oldValue: props.parent,
    page,
    pageId,
    value: parent,
  });
  getManager(item.type).onMove({
    appUid,
    pageId,
    oldValue: props.item,
    value: item,
  });
  set(pageAtom, page, { params: { appUid, pageId } });
}

export function insert<T>(
  collection: T[],
  atIndex: number,
  value: unknown,
): T[] {
  if (Array.isArray(collection)) {
    if (atIndex < collection.length) {
      const left = collection.slice(0, atIndex);
      const right = collection.slice(atIndex);
      return [...left, value, ...right] as unknown as T[];
    }
    if (atIndex === collection.length) {
      return [...collection, value] as unknown as T[];
    }
    throw Error(
      `Index out of bound! Index ${atIndex}. Size ${collection.length}.`,
    );
  }
  throw Error(
    'insert called on invalid collection. collection must be of type Array.',
  );
}

export const onDragAction = action(
  (
    { get, set },
    {
      appUid,
      pageId,
      itemId,
      hoverId,
      offset,
      rect,
      platform,
    }: {
      appUid: string;
      pageId: string;
      itemId: string;
      hoverId: string;
      offset: XYCoord;
      rect: DOMRect;
      platform: Platform;
    },
  ) => {
    if (!get(draggingAtom)) {
      return;
    }
    if (hoverId === '$clipboard.copy$') {
      set(dragTipAtom, 'Copy to Clipboard');
      set(clipboardIdAtom, hoverId);
      return;
    }
    if (hoverId === '$clipboard.move$') {
      set(dragTipAtom, 'Move to Clipboard');
      set(clipboardIdAtom, hoverId);
      return;
    }
    const clipboardId = get(clipboardIdAtom);
    if (clipboardId) {
      set(clipboardIdAtom, '');
    }
    const prevHoverId = get(prevHoverIdAtom);
    const prevXYCoord = get(prevXYCoordAtom);
    const initialXYCoord = get(initialXYCoordAtom);
    if (
      prevHoverId === hoverId &&
      performance.now() - get(prevTimeAtom) < 2000
    ) {
      return;
    }
    if (prevXYCoord.y === offset.y && prevXYCoord.x === offset.x) {
      return;
    }
    set(prevXYCoordAtom, offset);
    let vertical = false;
    if (
      Math.abs(initialXYCoord.y - offset.y) >
      Math.abs(initialXYCoord.x - offset.x)
    ) {
      // moving vertical
      vertical = true;
    }

    const up = vertical && initialXYCoord.y > offset.y;
    const down = vertical && initialXYCoord.y < offset.y;
    const left = !vertical && initialXYCoord.x > offset.x;
    const right = !vertical && initialXYCoord.x < offset.x;

    const page = get(pageAtom, { params: { appUid, pageId } });
    if (!page) {
      throw Error(`Missing page w/ id ${pageId}`);
    }
    const hoverItem = page[hoverId];
    let hoverMgr = getManager(hoverItem.type);
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    const itemMgr = getManager(item.type);

    if (item.parent === hoverItem.parent && hoverItem.itemId !== 'root') {
      if (
        hoverMgr.isChildAllowed(page, hoverItem, item.type, {
          appUid,
          pageId,
          itemId: hoverItem.itemId,
          mgr: hoverMgr,
          platform,
        }) === true &&
        itemMgr.isAllowedInsideParent(page, hoverItem, {
          appUid,
          pageId,
          itemId: hoverItem.itemId,
          mgr: itemMgr,
          platform,
        }) === true
      ) {
        move({ appUid, set, pageId, page, parent: hoverItem, item });
        set(prevTimeAtom, performance.now());
        set(initialXYCoordAtom, offset);
        return;
      }
      if (vertical) {
        if (up && offset.y >= rect.top + rect.height / 2) {
          // going up... wait till we cross the bottom half of the component hight
          set(
            dragTipAtom,
            `Drag further up... ${Math.round(
              Math.abs(rect.top + rect.height / 2 + 1 - offset.y),
            )}`,
          );
          return;
        }
        if (down && offset.y <= rect.top + rect.height / 2) {
          set(
            dragTipAtom,
            `Drag further down... ${Math.round(
              Math.abs(rect.top + rect.height / 2 - 1 - offset.y),
            )}`,
          );
          // going down... wait till we cross the top half of the component hight
          return;
        }
      } else {
        if (right && offset.x <= rect.left + rect.width / 2) {
          set(
            dragTipAtom,
            `Drag further right... ${Math.round(
              Math.abs(rect.left + rect.width / 2 + 1 - offset.x),
            )}`,
          );
          // going right... wait till we cross the right half of the component width
          return;
        }
        if (left && offset.x >= rect.left + rect.width / 2) {
          set(
            dragTipAtom,
            `Drag further left... ${Math.round(
              Math.abs(rect.left + rect.width / 2 - 1 - offset.x),
            )}`,
          );
          // going left... wait till we cross the left half of the component width
          return;
        }
      }
    }

    set(prevHoverIdAtom, hoverId);
    let prevTime = 0;

    if (!hoverId) {
      console.warn(`Missing hover item ${hoverId}!`);
      return;
    }

    if (hoverId === itemId) {
      // Hovering on same item!
      // this should never happen
      return;
    }

    if (hoverId === item.parent) {
      // item is already inside hoverId
      set(dragTipAtom, '');
      return;
    }

    if (hoverItem.parent === item.parent) {
      // re-order
      const parent = page[item.parent];
      const index = parent.children.indexOf(itemId);
      const hoverIndex = parent.children.indexOf(hoverId);
      let children = parent.children.filter((c) => c !== itemId);
      const targetIndex = hoverIndex + (hoverIndex < index && down ? 1 : 0);
      children = insert(children, targetIndex, itemId);
      const value = { ...parent, children };
      getManager(parent.type).onChildMoveIn({
        appUid,
        pageId,
        page,
        oldValue: parent,
        value,
        child: item,
      });
      set(designPath({ appUid, pageId, itemId: parent.itemId }), value);
      set(initialXYCoordAtom, offset);
      prevTime = performance.now();
    } else if (
      hoverMgr.isChildAllowed(page, hoverItem, item.type, {
        appUid,
        pageId,
        itemId: hoverItem.itemId,
        mgr: hoverMgr,
        platform,
      }) === true &&
      itemMgr.isAllowedInsideParent(page, hoverItem, {
        appUid,
        pageId,
        itemId: hoverItem.itemId,
        mgr: itemMgr,
        platform,
      }) === true
    ) {
      // move item inside hoverItem
      move({ appUid, set, pageId, page, parent: hoverItem, item });
      set(initialXYCoordAtom, offset);
      prevTime = performance.now();
    } else if (hoverItem.parent) {
      // move item inside hoverItem's parent
      const parent = page[hoverItem.parent];
      hoverMgr = getManager(parent.type);
      if (
        hoverMgr.isChildAllowed(page, parent, item.type, {
          appUid,
          pageId,
          itemId: parent.itemId,
          mgr: hoverMgr,
          platform,
        }) === true &&
        itemMgr.isAllowedInsideParent(page, parent, {
          appUid,
          pageId,
          itemId: parent.itemId,
          mgr: itemMgr,
          platform,
        }) === true
      ) {
        move({
          appUid,
          set,
          pageId,
          page,
          parent,
          item,
          hoverId: hoverItem.itemId,
        });
        set(initialXYCoordAtom, offset);
        prevTime = performance.now();
      } else {
        set(
          dragTipAtom,
          `${getComponentNameMap()[item.type]} inside ${
            getComponentNameMap()[parent.type]
          } is not allowed!`,
        );
      }
    }
    set(prevTimeAtom, prevTime);
  },
);

export function genId(page: Page): ItemID {
  let id = ulid().toLowerCase();
  while (page[id]) {
    id = ulid().toLowerCase();
  }
  return id;
}

export function copyItemBetweenPage(p: {
  fromPage: Page;
  itemId: ItemID;
  page: Page;
  newParentId: ItemID;
  retainRootItemId?: boolean;
  retainAllItemIds?: boolean;
  platform: Platform;
}): ItemID {
  const {
    platform,
    fromPage,
    itemId,
    page,
    newParentId,
    retainRootItemId,
    retainAllItemIds,
  } = p;
  const item = fromPage[itemId];
  const { type, children, props } = item;
  if (retainRootItemId && page[itemId]) {
    platform.showError(`Target page already contains item with id ${itemId}!`);
    throw new AbortError();
  }

  const newId =
    retainRootItemId || (retainAllItemIds && !page[itemId])
      ? itemId
      : genId(page);
  if (retainAllItemIds && page[itemId]) {
    platform.showWarning(
      `Item with id ${itemId} already exists in this page. Hence generated a new id ${newId}!`,
    );
  }

  const newChildren = children.map((child) =>
    copyItemBetweenPage({
      fromPage,
      itemId: child,
      page,
      newParentId: newId,
      retainAllItemIds,
      platform,
    }),
  );
  const updatedProps = getManager(type).onCopy({ ...props }, fromPage === page);

  // @ts-ignore TODO
  const itemCopy: AnyItem = {
    parent: newParentId,
    type,
    props: { ...updatedProps },
    itemId: newId,
    children: newChildren,
  };
  page[newId] = itemCopy as AnyItem;
  return newId;
}

export const duplicateAction = action(
  (
    { get, set },
    {
      appUid,
      itemId,
      pageId,
      platform,
    }: { appUid: string; itemId: string; pageId: string; platform: Platform },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    page = { ...page };
    const newId = copyItemBetweenPage({
      fromPage: page,
      itemId,
      page,
      newParentId: item.parent,
      platform,
    });
    const parentItem = page[item.parent];
    let { children } = parentItem;
    children = insert(children, children.indexOf(itemId) + 1, newId);
    page[item.parent] = { ...parentItem, children };
    set(designPath({ appUid, pageId }), page);
    set(
      activeItemSelector,
      {
        appUid,
        itemId: newId,
        pageId,
        expand: true,
        treename: TreeTypeDesigner,
      },
      {
        params: { appUid, treename: TreeTypeDesigner, pageId },
        props: platform,
      },
    );
  },
);

export const moveTopAction = action(
  (
    { get, set },
    {
      appUid,
      itemId,
      pageId,
    }: { appUid: string; itemId: string; pageId: string },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    const parentItem = page[item.parent];
    let { children } = parentItem;
    const index = children.indexOf(itemId);
    if (index > 0) {
      const element = children[index];
      children = [...children];
      children.splice(index, 1);
      children.splice(0, 0, element);

      page = { ...page };
      page[item.parent] = { ...parentItem, children };
      const mgr = getManager(parentItem.type);
      mgr.onChildMoveIn({
        appUid,
        child: item,
        oldValue: item,
        page,
        pageId,
        value: page[item.parent],
      });
      set(designPath({ appUid, pageId }), page);
    }
  },
);

export const moveUpAction = action(
  (
    { get, set },
    {
      itemId,
      pageId,
      appUid,
    }: { appUid: string; itemId: string; pageId: string },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    const parentItem = page[item.parent];
    let { children } = parentItem;
    const index = children.indexOf(itemId);
    if (index > 0) {
      const element = children[index];
      children = [...children];
      children.splice(index, 1);
      children.splice(index - 1, 0, element);

      page = { ...page };
      page[item.parent] = { ...parentItem, children };
      const mgr = getManager(parentItem.type);
      mgr.onChildMoveIn({
        appUid,
        child: item,
        oldValue: item,
        page,
        pageId,
        value: page[item.parent],
      });
      set(designPath({ appUid, pageId }), page);
    }
  },
);

export const moveDownAction = action(
  (
    { get, set },
    {
      appUid,
      itemId,
      pageId,
    }: { appUid: string; itemId: string; pageId: string },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    const parentItem = page[item.parent];
    let { children } = parentItem;
    const index = children.indexOf(itemId);
    if (index < children.length - 1) {
      const element = children[index];
      children = [...children];
      children.splice(index, 1);
      children.splice(index + 1, 0, element);

      page = { ...page };
      page[item.parent] = { ...parentItem, children };
      const mgr = getManager(parentItem.type);
      mgr.onChildMoveIn({
        appUid,
        child: item,
        oldValue: item,
        page,
        pageId,
        value: page[item.parent],
      });
      set(designPath({ appUid, pageId }), page);
    }
  },
);

export const moveBottomAction = action(
  (
    { get, set },
    {
      itemId,
      appUid,
      pageId,
    }: { appUid: string; itemId: string; pageId: string },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    const parentItem = page[item.parent];
    let { children } = parentItem;
    const index = children.indexOf(itemId);
    if (index < children.length - 1) {
      const element = children[index];
      children = [...children];
      children.splice(index, 1);
      children.splice(children.length, 0, element);

      page = { ...page };
      page[item.parent] = { ...parentItem, children };
      const mgr = getManager(parentItem.type);
      mgr.onChildMoveIn({
        appUid,
        child: item,
        oldValue: item,
        page,
        pageId,
        value: page[item.parent],
      });
      set(designPath({ appUid, pageId }), page);
    }
  },
);

export const pasteAction = action(
  (
    { get, set },
    {
      appUid,
      itemId,
      pageId,
      pasteAt,
      platform,
    }: {
      appUid: string;
      itemId: string;
      pageId: string;
      pasteAt: 'inside' | 'above' | 'below';
      platform: Platform;
    },
  ) => {
    const c = get(copyItemAtom);
    if (!c) {
      throw Error('Missing copy from item!');
    }
    const { pageId: fromPageId, itemId: fromItemId } = c;
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    page = { ...page };
    const parentItem = pasteAt === 'inside' ? item : page[item.parent];
    const fromPage =
      fromPageId === pageId
        ? page
        : (get(designPath({ appUid, pageId: fromPageId })) as Page);
    const newId = copyItemBetweenPage({
      fromPage,
      itemId: fromItemId,
      page,
      newParentId: parentItem.itemId,
      platform,
    });

    let { children } = parentItem;
    // eslint-disable-next-line default-case
    switch (pasteAt) {
      case 'below':
        children = insert(children, children.indexOf(itemId) + 1, newId);
        break;
      case 'above':
        children = insert(children, children.indexOf(itemId), newId);
        break;
      case 'inside':
        children = [...children, newId];
        break;
    }
    page[parentItem.itemId] = { ...parentItem, children };
    getManager(parentItem.type).onChildMoveIn({
      appUid,
      child: page[newId],
      oldValue: parentItem,
      page,
      pageId,
      value: page[parentItem.itemId],
    });
    set(designPath({ appUid, pageId }), page);
    set(
      activeItemSelector,
      {
        appUid,
        itemId: newId,
        pageId,
        expand: true,
        treename: TreeTypeDesigner,
      },
      {
        params: { treename: TreeTypeDesigner, pageId, appUid },
        props: platform,
      },
    );
  },
);

export function deleteChildrenItem(page: Page, itemId: ItemID) {
  const item = page[itemId];
  if (!item) return;
  const { children } = item;
  if (children) {
    children.forEach((child) => {
      deleteChildrenItem(page, child);
      delete page[child];
    });
  }
}

export function deleteItem(page: Page, itemId: ItemID) {
  const item = page[itemId];
  if (!item) return;
  const { children } = item;
  children.forEach((child) => deleteItem(page, child));
  delete page[itemId];
}

export const deleteAction = action(
  (
    { get, set },
    {
      appUid,
      itemId,
      pageId,
      platform,
    }: { appUid: string; itemId: string; pageId: string; platform: Platform },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = page[itemId];
    if (!item) {
      throw Error(`Missing item w/ id ${itemId}`);
    }
    page = { ...page };
    const copyItem = get(copyItemAtom);
    if (copyItem && copyItem.pageId !== '$$$copy$$$') {
      // keep a copy under a dummy page $$$copy$$$, so it can be pasted even after delete
      // mostly used to replace the component with a different type & re-add it into the newly added component
      set(designPath({ appUid, pageId: '$$$copy$$$' }), { ...page });
      set(copyItemAtom, { ...copyItem, pageId: '$$$copy$$$' });
    }
    deleteItem(page, itemId);
    const parentItem = page[item.parent];
    let nextItemId = '';
    // orphan item may not have the parentItem in this page
    if (parentItem) {
      let { children } = parentItem;
      let index = children.indexOf(itemId);
      children = children.filter((c) => c !== itemId);
      if (children.length === 0) {
        nextItemId = item.parent;
      } else {
        if (children.length <= index) {
          index = children.length - 1;
        }
        nextItemId = children[index];
      }
      page[item.parent] = { ...parentItem, children };
    }
    set(pageAtom, page, { params: { appUid, pageId } });

    let items = get(hoverItemsAtom);
    const hover = items.find((h) => h.itemId === itemId);
    if (hover) {
      items = items.slice(0, items.indexOf(hover));
      set(hoverItemsAtom, items);
    }
    const treename = TreeTypeDesigner;
    set(
      activeItemSelector,
      {
        appUid,
        itemId: nextItemId,
        pageId,
        expand: false,
        treename,
      },
      { params: { appUid, treename, pageId }, props: platform },
    );
    // remove(itemExpandedSelector,  { params: { pageId, itemId } });
  },
);

export const deleteChildrenAction = action(
  (
    { get, set },
    {
      appUid,
      itemId,
      platform,
      pageId,
    }: { appUid: string; itemId: string; pageId: string; platform: Platform },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const item = { ...page[itemId] };
    page = { ...page };
    item.children.forEach((child) => {
      deleteItem(page, child);
    });
    item.children = [];
    page[item.itemId] = item;
    set(designPath({ appUid, pageId }), page);
    set(copyItemAtom, undefined);
    set(
      activeItemSelector,
      { appUid, itemId, pageId, expand: false, treename: TreeTypeDesigner },
      {
        params: { appUid, treename: TreeTypeDesigner, pageId },
        props: platform,
      },
    );
  },
);

export async function autoPopulateChildren(props: {
  platform: Platform;
  page: Page;
  store: Store<unknown>;
  itemId: string;
  silent: boolean;
  appUid: string;
  pageId: string;
}): Promise<Page> {
  const { platform, store, itemId, silent, appUid, pageId } = props;
  let page = { ...props.page };
  const item = { ...page[itemId] };
  const attributes = await store.getAttributes();
  const pkAttributes = await store.getPrimaryKeyAttributes();
  const children = [...item.children];
  let newItemId = '';
  if (item.type === 'DataGrid' || item.type === 'CanvasGrid') {
    const attrs = attributes.filter((a) => {
      if (a.selectAllowed === 'N') {
        return false;
      }
      const child = children.find((c) => {
        const column = page[c] as Item<ColumnType>;
        return column.props.viewAttribute === a.attributeCode;
      });
      return !child;
    });
    const len = attrs.length;
    let newItem: Item<
      'StringColumn' | 'NumberColumn' | 'DateColumn' | 'CheckboxColumn'
    > | null = null;
    for (let i = 0; i < len; i++) {
      const attr = attrs[i];
      newItemId = '';
      newItem = null;
      if (
        isBooleanType(attr.attributeType) ||
        attr.attributeType === 'YN' ||
        attr.attributeType === 'TF'
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'CheckboxColumn',
          appUid,
          pageId,
        }));
        newItem.props.width = 80;
        if (attr.attributeType === 'TF') {
          (newItem as Item<'CheckboxColumn'>).props.checkedValue = 'T';
          (newItem as Item<'CheckboxColumn'>).props.unCheckedValue = 'F';
        }
      } else if (
        isStringType(attr.attributeType) ||
        attr.attributeType === 'JSON' ||
        (attr.attributeType === 'Reference' &&
          isStringType(attr.refMd?.attributeType ?? 'String'))
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'StringColumn',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'StringColumn'>;
        nItem.props.width = 200;
        nItem.props.maxLength = attr.maxLength ?? 200;
      } else if (
        isNumberType(attr.attributeType) ||
        (attr.attributeType === 'Reference' &&
          isNumberType(attr.refMd?.attributeType ?? 'String'))
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'NumberColumn',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'NumberColumn'>;
        nItem.props.width = 120;
        nItem.props.allowDecimal =
          attr.attributeType === 'Decimal' ||
          attr.attributeType === 'Percent' ||
          attr.attributeType === 'Double';
      } else if (isDateType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'DateColumn',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'DateColumn'>;
        nItem.props.width = 200;
        nItem.props.showTime = attr.attributeType === 'DateTime';
      } else {
        console.warn('Skipping: ', attr);
      }
      if (newItemId && newItem) {
        const pkAttr = pkAttributes.find(
          (a) => a.attributeCode === attr.attributeCode,
        );
        if (pkAttr) {
          newItem.props.updatable = { value: false, type: 'boolean' };
        }
        newItem.props.viewAttribute = attr.attributeCode;
        newItem.props.label = { type: 'text', value: attr.attributeName };
        newItem.props.required = attr.allowBlank === 'N';
        if (attr.queryAllowed === 'N') {
          newItem.props.filterable = false;
          newItem.props.sortable = false;
        }
        if (attr.attributeType === 'JSON' || isWhoAttribute(attr)) {
          newItem.props.insertable = { value: false, type: 'boolean' };
          newItem.props.updatable = { value: false, type: 'boolean' };
        } else {
          newItem.props.insertable = {
            value: attr.insertAllowed === 'Y',
            type: 'boolean',
          };
          newItem.props.updatable = {
            value: attr.updateAllowed === 'Y',
            type: 'boolean',
          };
        }
        children.push(newItemId);
      }
    }
    if (len && !silent) {
      platform.showSuccess(`Auto populated ${len} Columns!`);
    }
  } else if (item.type === 'SmartSearch') {
    const attrs = attributes.filter((a) => {
      if (a.selectAllowed === 'N' || a.queryAllowed === 'N') {
        return false;
      }
      const child = children.find((c) => {
        const column = page[c] as Item<'SmartSearchAttribute'>;
        return column.props.attributeCode === a.attributeCode;
      });
      return !child;
    });
    const len = attrs.length;
    let newItem: Item<'SmartSearchAttribute'> | null = null;
    for (let i = 0; i < len; i++) {
      const attr = attrs[i];
      newItemId = '';
      newItem = null;
      ({ page, newItemId, newItem } = createItem({
        platform,
        page,
        parentId: itemId,
        itemType: 'SmartSearchAttribute',
        appUid,
        pageId,
      }));
      if (newItemId && newItem) {
        const normalizedAttributeType = getFilterDataType(attr);
        const dataType = toAttributeDataType(normalizedAttributeType);
        if (dataType) {
          newItem.props.dataType = dataType;
          newItem.props.defaultOperator = defaultOperators[dataType];
        } else {
          console.warn('Skipping: ', attr);
        }
        newItem.props.attributeCode = attr.attributeCode;
        newItem.props.label = attr.attributeName;
        children.push(newItemId);
      }
    }
    if (len && !silent) {
      platform.showSuccess(`Auto populated ${len} Search Attributes!`);
    }
  } else if (item.type === 'Form') {
    const attrs = attributes.filter((a) => {
      const child = children.find((c) => {
        const column = page[c] as Item<ColumnType>;
        return column.props.viewAttribute === a.attributeCode;
      });
      return !child;
    });
    const len = attrs.length;
    let newItem: Item<
      | 'AttachmentField'
      | 'Checkbox'
      | 'DateField'
      | 'NumberField'
      | 'RadioBox'
      | 'Select'
      | 'TextField'
    > | null = null;
    for (let i = 0; i < len; i++) {
      const attr = attrs[i];
      newItemId = '';
      newItem = null;
      if (attr.attributeType === 'Attachment') {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'AttachmentField',
          appUid,
          pageId,
        }));
      } else if (
        isBooleanType(attr.attributeType) ||
        attr.attributeType === 'YN' ||
        attr.attributeType === 'TF'
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'Checkbox',
          appUid,
          pageId,
        }));
        if (attr.attributeType === 'TF') {
          (newItem as Item<'Checkbox'>).props.checkedValue = 'T';
          (newItem as Item<'Checkbox'>).props.unCheckedValue = 'F';
        }
      } else if (isStringType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'TextField',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'TextField'>;
        if (attr.maxLength) {
          nItem.props.maxLength = attr.maxLength;
          if (attr.maxLength > 512) {
            nItem.props.multiline = true;
          }
        }
      } else if (isNumberType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'NumberField',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'NumberField'>;
        nItem.props.allowDecimal =
          attr.attributeType === 'Decimal' ||
          attr.attributeType === 'Percent' ||
          attr.attributeType === 'Double';
      } else if (isDateType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'DateField',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'DateField'>;
        nItem.props.showTime = attr.attributeType === 'DateTime';
      } else {
        console.warn('Skipping: ', attr);
      }
      if (newItemId && newItem) {
        const pkAttr = pkAttributes.find(
          (a) => a.attributeCode === attr.attributeCode,
        );
        if (pkAttr) {
          newItem.props.updatable = { value: false, type: 'boolean' };
        }
        newItem.props.viewAttribute = attr.attributeCode;
        newItem.props.label = { type: 'text', value: attr.attributeName };
        newItem.props.required = attr.allowBlank === 'N';

        if (isWhoAttribute(attr)) {
          newItem.props.insertable = { value: false, type: 'boolean' };
          newItem.props.updatable = { value: false, type: 'boolean' };
        } else {
          newItem.props.insertable = {
            value: attr.insertAllowed === 'Y',
            type: 'boolean',
          };
          newItem.props.updatable = {
            value: attr.updateAllowed === 'Y',
            type: 'boolean',
          };
        }

        if (attr.primaryKey === 'Y') {
          newItem.props.updatable = { value: false, type: 'boolean' };
        }

        children.push(newItemId);
      }
    }
    if (len && !silent) {
      platform.showSuccess(`Auto populated ${len} Fields!`);
    }
  } else if (item.type === 'SearchForm') {
    const attrs = attributes.filter((a) => {
      const child = children.find((c) => {
        const column = page[c] as Item<ColumnType>;
        return column.props.viewAttribute === a.attributeCode;
      });
      return !child;
    });
    const len = attrs.length;
    let newItem: Item<
      | 'SearchCheckbox'
      | 'SearchDateField'
      | 'SearchLookupField'
      | 'SearchNumberField'
      | 'SearchSelectField'
      | 'SearchTextField'
    > | null = null;
    for (let i = 0; i < len; i++) {
      const attr = attrs[i];
      newItemId = '';
      newItem = null;
      if (attr.attributeType === 'Attachment') {
        continue;
      } else if (
        isBooleanType(attr.attributeType) ||
        attr.attributeType === 'YN' ||
        attr.attributeType === 'TF'
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'SearchCheckbox',
          appUid,
          pageId,
        }));
      } else if (isStringType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'SearchTextField',
          appUid,
          pageId,
        }));
        newItem as Item<'SearchTextField'>;
      } else if (isNumberType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'SearchNumberField',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'SearchNumberField'>;
        nItem.props.allowDecimal =
          attr.attributeType === 'Decimal' ||
          attr.attributeType === 'Percent' ||
          attr.attributeType === 'Double';
      } else if (isDateType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'SearchDateField',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'SearchDateField'>;
        nItem.props.showTime = attr.attributeType === 'DateTime';
      } else {
        console.warn('Skipping: ', attr);
      }
      if (newItemId && newItem) {
        newItem.props.viewAttribute = attr.attributeCode;
        newItem.props.label = { type: 'text', value: attr.attributeName };
        const normalizedAttributeType = getFilterDataType(attr);
        newItem.props.dataType = toAttributeDataType(normalizedAttributeType);
        children.push(newItemId);
      }
    }
    if (len && !silent) {
      platform.showSuccess(`Auto populated ${len} Fields!`);
    }
  } else if (item.type === 'PivotTable') {
    const attrs = attributes.filter((a) => {
      if (a.selectAllowed === 'N') {
        return false;
      }
      const child = children.find((c) => {
        const column = page[c] as Column;
        return column.props.viewAttribute === a.attributeCode;
      });
      return !child;
    });
    const len = attrs.length;
    let newItem: Item<
      | 'PivotTableDateColumn'
      | 'PivotTableNumberColumn'
      | 'PivotTableStringColumn'
    > | null = null;
    for (let i = 0; i < len; i++) {
      const attr = attrs[i];
      newItemId = '';
      newItem = null;
      if (
        isBooleanType(attr.attributeType) ||
        attr.attributeType === 'YN' ||
        attr.attributeType === 'TF'
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'PivotTableStringColumn',
          appUid,
          pageId,
        }));
        newItem.props.columnWidth = 80;
      } else if (
        isStringType(attr.attributeType) ||
        attr.attributeType === 'JSON' ||
        (attr.attributeType === 'Reference' &&
          isStringType(attr.refMd?.attributeType ?? 'String'))
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'PivotTableStringColumn',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'PivotTableStringColumn'>;
        nItem.props.columnWidth = 200;
      } else if (
        isNumberType(attr.attributeType) ||
        (attr.attributeType === 'Reference' &&
          isNumberType(attr.refMd?.attributeType ?? 'String'))
      ) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'PivotTableNumberColumn',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'PivotTableNumberColumn'>;
        nItem.props.columnWidth = 120;
      } else if (isDateType(attr.attributeType)) {
        ({ page, newItemId, newItem } = createItem({
          platform,
          page,
          parentId: itemId,
          itemType: 'PivotTableDateColumn',
          appUid,
          pageId,
        }));
        const nItem = newItem as Item<'PivotTableDateColumn'>;
        nItem.props.columnWidth = 120;
      } else {
        console.warn('Skipping: ', attr);
      }
      if (newItemId && newItem) {
        newItem.props.viewAttribute = attr.attributeCode;
        newItem.props.title = attr.attributeName;
        children.push(newItemId);
      }
    }
    if (len && !silent) {
      platform.showSuccess(`Auto populated ${len} Columns!`);
    }
  }
  item.children = children;
  page[item.itemId] = item;
  return page;
}

export const autoPopulateChildrenAction = action(
  async (
    { get, set },
    {
      platform,
      itemId,
      pageId,
      appUid,
      silent = false,
    }: {
      platform: Platform;
      itemId: string;
      pageId: string;
      appUid: string;
      silent?: boolean;
    },
  ) => {
    let page = get(designPath({ appUid, pageId })) as Page;
    const store = platform.getParentStore(appUid, pageId, itemId);
    if (!store) return;
    page = await autoPopulateChildren({
      platform,
      page,
      store,
      itemId,
      silent,
      appUid,
      pageId,
    });
    set(designPath({ appUid, pageId }), page);
  },
);

export function getChildrenOfType<T extends ItemType>(
  page: Page,
  item: AnyItem,
  type: string,
): Item<T>[] {
  return item.children
    .map((c) => page[c])
    .filter((c) => c.type === type) as Item<T>[];
}

export interface SidebarState {
  open: boolean;
  width: number;
  exists: boolean;
  pageId: string;
  minimized: boolean;
  padding?: Padding;
}

export const sidebarStateAtom = atom<SidebarState>({
  path: ['transient', 'sidebarComp', ':appUid'],
  defaultValue: {
    open: true,
    width: 260,
    exists: false,
    pageId: '',
    minimized: false,
    padding: { all: 0 },
  },
});

export const sidebarMinimizedStateAtom = atom({
  path: ['transient', 'sidebarComp', ':appUid', 'minimized'],
  defaultValue: false,
});

export const replaceItemAction = action(
  async (
    { get, set },
    {
      itemId,
      pageId,
      appUid,
      itemType,
    }: {
      itemId: string;
      pageId: string;
      appUid: string;
      itemType: ItemType;
    },
  ) => {
    let _page = get(pageAtom, { params: { appUid, pageId } });
    if (!_page) {
      return;
    }
    _page = { ..._page };
    replaceItemType(_page, itemId, itemType);
    // force re-render of the parent
    set(hmrAtom, (v) => v + 1);
    set(pageAtom, _page, {
      params: {
        appUid,
        pageId,
      },
    });
  },
);

export function replaceItemType(
  _page: Page,
  itemId: string,
  itemType: ItemType,
) {
  const origItem = _page[itemId];
  const toMgr = getManager(itemType);
  const fromMgr = getManager(origItem.type);
  const parentItem = _page[origItem.parent];
  _page[origItem.parent] = {
    ...parentItem,
    children: [...parentItem.children],
  };
  let _item = { ...origItem };
  const props = { ..._item.props };
  Object.keys(props).forEach((key) => {
    if (
      // @ts-ignore dynamic key
      !toMgr.props[key] ||
      typeof toMgr.defaultValue[key] !== typeof fromMgr.defaultValue[key]
    ) {
      // @ts-ignore dynamic key
      delete props[key];
    }
  });
  _item.props = props;
  _item.type = toMgr.type;
  // this will not cause re-render as StateX compares the array with JSON.stringify
  // hence also setting hmrAtom as a workaround
  _item.children = [..._item.children];
  _item = toMgr.onReplace(_item, itemWithDefaultValues(origItem), _page);
  _page[itemId] = _item;
}
